// Right-side sheet for LLM-assisted voice prompt refinement.
//
// Flow:
//   1. Admin types an instruction ("be less wordy in the opening").
//   2. POST /refine — backend grabs current prompt + last 2 transcripts,
//      masks placeholders, asks Claude for a refinement, validates that
//      every {firmName} / {{currentDate}} round-tripped, returns the
//      suggestion + a basedOnPromptHash for stale-write protection.
//   3. Admin sees a side-by-side line diff, with placeholders rendered as
//      non-editable pill chips so it's visually obvious they're frozen.
//   4. Admin clicks Approve → POST /refine/apply → backend re-validates,
//      saves to custom_base_prompt, syncs to Telnyx, logs to prompt_edit_log.

import { useMemo, useState } from 'react'
import { Sparkles, Loader2, Check, X } from 'lucide-react'
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import api from '@/lib/api'

interface CallUsed {
  id: string
  startedAt: string | null
  durationSec: number | null
}

interface RefineResponse {
  success: boolean
  suggestedPrompt: string
  rationale: string
  changes: string[]
  callsUsed: CallUsed[]
  basedOnPromptHash: string
}

interface Props {
  open: boolean
  onClose: () => void
  orgId: string
  assistantId: string
  assistantName: string
  currentPrompt: string
  onApplied: () => void
}

export default function PromptRefineDrawer({
  open, onClose, orgId, assistantId, assistantName, currentPrompt, onApplied,
}: Props) {
  const { toast } = useToast()
  const [instruction, setInstruction] = useState('')
  const [generating, setGenerating] = useState(false)
  const [applying, setApplying] = useState(false)
  const [result, setResult] = useState<RefineResponse | null>(null)

  const reset = () => {
    setInstruction('')
    setResult(null)
  }

  const generate = async () => {
    if (instruction.trim().length < 3) {
      toast({ title: 'Add a bit more detail', description: 'Tell the model what you want refined.', variant: 'destructive' })
      return
    }
    setGenerating(true)
    try {
      const res = await api.post<RefineResponse>(
        `/api/platform/organizations/${orgId}/prompts/voice/${assistantId}/refine`,
        { instruction },
        // Override the global 10s axios timeout — Sonnet takes 30-90s to
        // generate a full refined prompt + rationale.
        { timeout: 180_000 },
      )
      setResult(res.data)
    } catch (err: any) {
      const status = err?.response?.status
      const msg = err?.response?.data?.error || err?.message || 'Refinement request failed'
      toast({
        title: status === 422 ? 'Suggestion rejected — placeholders changed' : 'Could not generate refinement',
        description: msg,
        variant: 'destructive',
      })
    } finally {
      setGenerating(false)
    }
  }

  const apply = async () => {
    if (!result) return
    setApplying(true)
    try {
      const res = await api.post(
        `/api/platform/organizations/${orgId}/prompts/voice/${assistantId}/refine/apply`,
        {
          suggestedPrompt: result.suggestedPrompt,
          instruction,
          basedOnPromptHash: result.basedOnPromptHash,
        },
        // Override the global 10s axios timeout — apply does a DB write +
        // Telnyx PATCH; Telnyx can be slow under load.
        { timeout: 60_000 },
      )
      const syncStatus = res.data?.syncStatus
      if (syncStatus === 'failed') {
        toast({
          title: 'Saved — but Telnyx sync FAILED',
          description: res.data?.syncError || 'DB updated, Telnyx still on old prompt. Try Push to Telnyx.',
          variant: 'destructive',
        })
      } else if (syncStatus === 'no_provider') {
        toast({ title: 'Saved (no Telnyx agent attached)' })
      } else {
        toast({ title: 'Refinement applied', description: 'Prompt updated and pushed to Telnyx.' })
      }
      onApplied()
      reset()
      onClose()
    } catch (err: any) {
      const status = err?.response?.status
      const msg = err?.response?.data?.error || err?.message || 'Apply failed'
      toast({
        title: status === 409 ? 'Prompt changed since suggestion was generated' : 'Could not apply',
        description: status === 409 ? 'Regenerate the suggestion to pick up the latest version.' : msg,
        variant: 'destructive',
      })
    } finally {
      setApplying(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) { reset(); onClose() } }}>
      <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-purple-600" />
            Refine prompt with AI — {assistantName}
          </SheetTitle>
          <SheetDescription>
            Describe what to change. The model reads the last 2 real call transcripts for context. Placeholders like
            <code className="mx-1 px-1.5 py-0.5 rounded bg-muted text-[11px]">{'{firmName}'}</code>
            and
            <code className="mx-1 px-1.5 py-0.5 rounded bg-muted text-[11px]">{'{{currentDate}}'}</code>
            are frozen — the model never sees them and can't change them.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">What should we refine?</label>
            <Textarea
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              placeholder="e.g. Be less wordy in the opening — one short sentence, then ask for the caller's name."
              rows={3}
              disabled={generating || applying}
            />
            <div className="flex justify-end mt-2">
              <Button onClick={generate} disabled={generating || applying || instruction.trim().length < 3}>
                {generating ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Reading recent calls…</> : <><Sparkles className="h-3.5 w-3.5 mr-2" /> Generate suggestion</>}
              </Button>
            </div>
          </div>

          {result && (
            <div className="space-y-4 border-t pt-4">
              {result.callsUsed.length > 0 ? (
                <div className="text-[11px] text-muted-foreground">
                  Used {result.callsUsed.length} recent call{result.callsUsed.length === 1 ? '' : 's'}:
                  {' '}
                  {result.callsUsed.map((c) => `${c.startedAt ? new Date(c.startedAt).toLocaleString() : 'unknown'} (${c.durationSec ?? '?'}s)`).join(' • ')}
                </div>
              ) : (
                <div className="text-[11px] text-amber-600">No recent transcripts found — suggestion is based on the prompt alone.</div>
              )}

              {(result.rationale || result.changes.length > 0) && (
                <div className="rounded-md border bg-muted/30 p-3 space-y-2">
                  {result.rationale && (
                    <div>
                      <div className="text-[11px] font-semibold text-muted-foreground mb-0.5">Why</div>
                      <div className="text-xs">{result.rationale}</div>
                    </div>
                  )}
                  {result.changes.length > 0 && (
                    <div>
                      <div className="text-[11px] font-semibold text-muted-foreground mb-0.5">Changes</div>
                      <ul className="text-xs list-disc pl-5 space-y-0.5">
                        {result.changes.map((c, i) => <li key={i}>{c}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <DiffView original={currentPrompt} suggestion={result.suggestedPrompt} />

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="ghost" onClick={() => setResult(null)} disabled={applying}>
                  <X className="h-3.5 w-3.5 mr-1.5" /> Try again
                </Button>
                <Button variant="outline" onClick={() => { reset(); onClose() }} disabled={applying}>
                  Discard
                </Button>
                <Button onClick={apply} disabled={applying}>
                  {applying ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Applying…</> : <><Check className="h-3.5 w-3.5 mr-2" /> Approve & sync</>}
                </Button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

// ── Diff view ────────────────────────────────────────────────────────────
//
// Line-level diff using LCS. Placeholders are rendered as pill chips inside
// each line so the admin sees exactly which tokens are frozen and which
// surrounding text actually changed.

function DiffView({ original, suggestion }: { original: string; suggestion: string }) {
  const lines = useMemo(() => computeDiff(original, suggestion), [original, suggestion])

  return (
    <div>
      <div className="grid grid-cols-2 gap-0 text-[11px] font-mono border rounded-md overflow-hidden">
        <div className="bg-muted/40 px-2 py-1 font-semibold border-b border-r text-muted-foreground">Current</div>
        <div className="bg-muted/40 px-2 py-1 font-semibold border-b text-muted-foreground">Suggested</div>
        {lines.map((row, i) => (
          <DiffRow key={i} row={row} />
        ))}
      </div>
    </div>
  )
}

function DiffRow({ row }: { row: DiffLine }) {
  const leftClass = row.left.kind === 'remove' ? 'bg-red-50 dark:bg-red-950/20' : row.left.kind === 'context' ? '' : 'bg-muted/10'
  const rightClass = row.right.kind === 'add' ? 'bg-green-50 dark:bg-green-950/20' : row.right.kind === 'context' ? '' : 'bg-muted/10'
  return (
    <>
      <div className={`px-2 py-0.5 border-r whitespace-pre-wrap break-words ${leftClass}`}>
        {row.left.text !== null ? <RenderWithChips text={row.left.text} /> : <span className="text-muted-foreground italic">·</span>}
      </div>
      <div className={`px-2 py-0.5 whitespace-pre-wrap break-words ${rightClass}`}>
        {row.right.text !== null ? <RenderWithChips text={row.right.text} /> : <span className="text-muted-foreground italic">·</span>}
      </div>
    </>
  )
}

// Render text with {x} and {{x}} replaced by visual chips. The chip text is
// not editable in this view (the diff is read-only anyway), and stands out so
// admins can confirm placeholders are intact.
function RenderWithChips({ text }: { text: string }) {
  const parts = useMemo(() => splitForChips(text), [text])
  return (
    <>
      {parts.map((p, i) => p.type === 'chip'
        ? <span key={i} className="inline-block px-1 py-0 mx-0.5 rounded bg-purple-100 dark:bg-purple-900/40 text-purple-900 dark:text-purple-200 border border-purple-300 dark:border-purple-700 text-[10px] font-medium align-baseline">{p.value}</span>
        : <span key={i}>{p.value}</span>
      )}
    </>
  )
}

function splitForChips(text: string): Array<{ type: 'text' | 'chip'; value: string }> {
  // Match double-brace first, then single-brace.
  const tokenRegex = /(\{\{\s*[a-zA-Z][a-zA-Z0-9_]*\s*\}\}|\{[a-zA-Z][a-zA-Z0-9_]*\})/g
  const parts: Array<{ type: 'text' | 'chip'; value: string }> = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = tokenRegex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push({ type: 'text', value: text.slice(lastIndex, match.index) })
    parts.push({ type: 'chip', value: match[0] })
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < text.length) parts.push({ type: 'text', value: text.slice(lastIndex) })
  return parts
}

interface DiffSide { text: string | null; kind: 'context' | 'add' | 'remove' }
interface DiffLine { left: DiffSide; right: DiffSide }

function computeDiff(a: string, b: string): DiffLine[] {
  const aLines = a.split('\n')
  const bLines = b.split('\n')

  // LCS table.
  const m = aLines.length
  const n = bLines.length
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0))
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      if (aLines[i] === bLines[j]) dp[i][j] = dp[i + 1][j + 1] + 1
      else dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1])
    }
  }

  const result: DiffLine[] = []
  let i = 0
  let j = 0
  while (i < m && j < n) {
    if (aLines[i] === bLines[j]) {
      result.push({ left: { text: aLines[i], kind: 'context' }, right: { text: bLines[j], kind: 'context' } })
      i++; j++
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      result.push({ left: { text: aLines[i], kind: 'remove' }, right: { text: null, kind: 'context' } })
      i++
    } else {
      result.push({ left: { text: null, kind: 'context' }, right: { text: bLines[j], kind: 'add' } })
      j++
    }
  }
  while (i < m) { result.push({ left: { text: aLines[i++], kind: 'remove' }, right: { text: null, kind: 'context' } }) }
  while (j < n) { result.push({ left: { text: null, kind: 'context' }, right: { text: bLines[j++], kind: 'add' } }) }
  return result
}
